import React, {
  useState,
  useCallback,
  useMemo,
  useRef,
  useEffect,
} from "react";
import { useNavigate } from "react-router-dom";
import { atom, useAtom } from "jotai";
import BurndownChart from "./BurndownChart";
import AskCalculatorModal from "./AskCalculatorModal";
import { TimelineZoom, type ZoomLevel } from "../atoms";
import {
  buildDefaultCustomRange,
  calculateVisibleDateRange,
  panCustomRange,
  panDateByZoom,
  DEFAULT_ZOOM_LEVEL,
  type TimelineCustomRange,
} from "../../utils/chart/timeline-range.utils";
import {
  clampDateToRange,
  getDefaultTimelineDate,
} from "../../utils/chart/timeline-date.utils";
import { calculateBurndownSnapshot } from "../../utils/chart/burndown.utils";
import {
  loadTimelineRangePreference,
  saveTimelineRangePreference,
} from "../../utils/chart/timeline-preference.utils";
import { useStartupContext } from "../../hooks/useStartupContext";
import { useAuth } from "../../hooks/useAuth.hook";
import {
  useCompleteStartupData,
  useCreateExpense,
  useUpdateExpense,
  useDeleteExpense,
  useCreateRevenue,
  useUpdateRevenue,
  useDeleteRevenue,
  useCreateEvent,
  useUpdateEvent,
  useDeleteEvent,
  useReviseAssetEvent,
  useReviseRevenue,
  useReviseExpense,
} from "../../hooks/useFinancialData";
import {
  useCustomerProjections,
  useSubscriptionTiers,
  useCustomerGrowthScenarios,
  useAcquisitionCostModels,
  useCapacityGroups,
  useCreateCapacityGroup,
  useUpdateCapacityGroup,
  useDeleteCapacityGroup,
  useCreateCustomerProjection,
  useUpdateCustomerProjection,
  useDeleteCustomerProjection,
  useCreateSubscriptionTier,
  useUpdateSubscriptionTier,
  useDeleteSubscriptionTier,
} from "../../hooks/useCustomerData";
import {
  Modal,
  ExpenseForm,
  RevenueForm,
  EventForm,
  CustomerProjectionForm,
  SubscriptionTierForm,
  CapacityGroupForm,
  QuickAddBar,
  BulkDateShiftModal,
  StartupSetupHero,
} from "../molecules";
import { AssetRevisionForm } from "../molecules/AssetRevisionForm";
import { RevenueRevisionForm } from "../molecules/RevenueRevisionForm";
import { ExpenseRevisionForm } from "../molecules/ExpenseRevisionForm";
import { LabeledFabAction } from "@/components/molecules/LabeledFabAction";
import {
  MobileFinancialSummary,
  MobileActionButtons,
  MobileTimelineControls,
  MobileCharts,
  MobileBurndownChart,
} from "../mobile";
import { ChatInterface } from "../chat";
import { ProUpgradeModal, ProUpgradeStrip } from "@/components/molecules/ProUpgradeFunnel";
import { useProAccess } from "@/hooks/useProAccess";
import {
  DollarSign,
  TrendingUp,
  Calendar,
  MessageCircle,
  Users,
  Package,
  CalendarClock,
  Calculator,
  Plus,
  X,
} from "lucide-react";
import type {
  CreateExpenseData,
  CreateRevenueData,
  CreateEventData,
  ReviseAssetEventData,
  ReviseRevenueData,
  ReviseExpenseData,
} from "../../services/api/financial.service";
import type {
  CustomerProjection,
  SubscriptionTier,
  CustomerGrowthScenario,
  AcquisitionCostModel,
  CustomerCapacityGroup,
  CreateCustomerCapacityGroupData,
  CreateCustomerProjectionData,
} from "../../types/startup.types";
import { buildCapacityEventsFromTimelineEvents } from "../../utils/chart/customer-projection.utils";
import type { TimelineSnapshot } from "../../types/burndown.types";
import type {
  Expense,
  Revenue,
  FinancialEvent,
} from "../../types/startup.types";
import { safeDate, parseCalendarDate, toDateInputValue } from "../../utils/date";
import { PROJECTED_CUSTOMER_ACQUISITION_EXPENSE_ID } from "../../utils/chart/acquisition-expense.utils";

import { getOpenSegmentTarget, canReviseSegment, hasLaterSegments, confirmEditSupersededSegment } from "../../utils/chart/line-item-revision.utils";
import type { RevisionItemKind } from "../../utils/chart/segment-grouping.utils";

function getOpenAssetSegmentTarget(
  event: FinancialEvent,
  events: FinancialEvent[],
): FinancialEvent | null {
  return getOpenSegmentTarget(
    { ...event, startDate: event.date },
    events.map((e) => ({ ...e, startDate: e.date })),
  );
}

// Import types
// Jotai atom for timeline snapshot - survives React re-renders
const timelineSnapshotAtom = atom<TimelineSnapshot | null>(null);

// Sample data for demonstration
const generateSampleData = () => {
  const startDate = new Date("2024-01-01");
  const endDate = new Date("2028-12-31");

  // Sample expenses with start/end dates
  const expenses = [
    {
      id: "1",
      startDate: new Date("2024-01-01"),
      monthlyAmount: 5000,
      category: "Salary",
      name: "Developer Salary",
      color: "#dc2626",
    },
    {
      id: "2",
      startDate: new Date("2024-02-01"),
      monthlyAmount: 1500,
      category: "Marketing",
      name: "Google Ads",
      color: "#059669",
    },
    {
      id: "3",
      startDate: new Date("2024-03-01"),
      monthlyAmount: 800,
      dayOfMonth: 20,
      category: "Operations",
      name: "Office Rent",
      color: "#d97706",
    },
    {
      id: "3.1",
      startDate: new Date("2024-02-01"),
      monthlyAmount: 400,
      dayOfMonth: 15,
      category: "Operations",
      name: "Cloud Fees",
      color: "#d97706",
    },
    {
      id: "3.2",
      startDate: new Date("2024-02-01"),
      monthlyAmount: 400,
      dayOfMonth: 18,
      category: "Operations",
      name: "Internet Service",
      color: "#d97706",
    },
    {
      id: "4",
      startDate: new Date("2024-06-01"),
      endDate: new Date("2024-12-01"),
      monthlyAmount: 1200,
      category: "Product",
      name: "Software Licenses",
      color: "#7c3aed",
    },
    {
      id: "5",
      startDate: new Date("2024-09-01"),
      monthlyAmount: 3000,
      category: "Salary",
      name: "Marketing Manager",
      color: "#dc2626",
    },
  ];

  const revenues = [
    {
      id: "r1",
      name: "Monthly SaaS Revenue",
      category: "sales" as const,
      monthlyAmount: 2000,
      startDate: new Date("2024-02-01"),
      endDate: new Date("2025-02-01"),
      isRecurring: true,
      dayOfMonth: 5,
      frequency: "monthly" as const,
      color: "#10b981",
    },
    {
      id: "r1.1",
      name: "Monthly SaaS Revenue",
      category: "sales" as const,
      monthlyAmount: 10000,
      startDate: new Date("2025-03-01"),
      isRecurring: true,
      dayOfMonth: 5,
      frequency: "monthly" as const,
      color: "#10b981",
    },
    {
      id: "r2",
      name: "Consulting Services",
      category: "sales" as const,
      monthlyAmount: 3000,
      startDate: new Date("2024-04-01"),
      endDate: new Date("2024-08-01"),
      isRecurring: true,
      frequency: "monthly" as const,
      color: "#3b82f6",
    },
    {
      id: "r3",
      name: "Grant Funding",
      category: "grant" as const,
      monthlyAmount: 1500,
      startDate: new Date("2024-01-01"),
      endDate: new Date("2024-06-01"),
      isRecurring: true,
      dayOfMonth: 10,
      frequency: "monthly" as const,
      color: "#8b5cf6",
    },
  ];

  // Sample events with funding amounts
  const events = [
    {
      id: "1",
      date: new Date("2024-08-01"),
      type: "funding" as const,
      priority: "high" as const,
      description: "Seed funding round - $250K",
      amount: 250000, // $250K one-time funding injection
      icon: "DollarSign", // Custom Lucide icon
      color: "#16a34a", // Custom green color
    },
    {
      id: "2",
      date: new Date("2024-10-01"),
      type: "launch" as const,
      priority: "medium" as const,
      description: "Product launch",
      icon: "Rocket", // Custom Lucide icon
      color: "#dc2626", // Custom red color
    },
    {
      id: "3",
      date: new Date("2025-03-01"),
      type: "milestone" as const,
      priority: "low" as const,
      description: "Break-even target",
      icon: "Target", // Custom Lucide icon
      color: "#7c3aed", // Custom purple color
    },
  ];

  return {
    startDate,
    endDate,
    initialBalance: 100000, // Starting with $100K
    expenses,
    events,
    revenues,
  };
};

const BurndownChartDemo: React.FC = () => {
  const navigate = useNavigate();
  const { currentStartup, isLoading: startupsLoading, openCreateStartupModal } =
    useStartupContext();
  const { firebaseUser } = useAuth();
  const { isPro } = useProAccess();
  const startupId = currentStartup?.id || "";

  const { data: startupData, isLoading } = useCompleteStartupData(startupId);

  // Desktop floating action buttons state
  const [isFloatingMenuExpanded, setIsFloatingMenuExpanded] = useState(false);
  const [isBulkShiftOpen, setIsBulkShiftOpen] = useState(false);

  // Customer data - re-enabled for full functionality
  const { data: customerProjectionsData = [] } =
    useCustomerProjections(startupId);
  const { data: subscriptionTiersData = [] } = useSubscriptionTiers(startupId);
  const { data: customerGrowthScenariosData = [] } =
    useCustomerGrowthScenarios(startupId);
  const { data: acquisitionCostModelsData = [] } =
    useAcquisitionCostModels(startupId);
  const { data: capacityGroupsData = [] } = useCapacityGroups(startupId);

  // Transform API data to match local types (convert string dates to Date objects)
  const customerProjections: CustomerProjection[] = customerProjectionsData.map(
    (proj) => ({
      ...proj,
      startDate: new Date(proj.startDate),
      endDate: proj.endDate ? new Date(proj.endDate) : undefined,
      createdAt: new Date(proj.createdAt),
      updatedAt: new Date(proj.updatedAt),
    })
  );

  const subscriptionTiers: SubscriptionTier[] = subscriptionTiersData.map(
    (tier) => ({
      ...tier,
      createdAt: new Date(tier.createdAt),
      updatedAt: new Date(tier.updatedAt),
    })
  );

  const customerGrowthScenarios: CustomerGrowthScenario[] =
    customerGrowthScenariosData.map((scenario) => ({
      ...scenario,
      startDate: new Date(scenario.startDate),
      endDate: scenario.endDate ? new Date(scenario.endDate) : undefined,
      createdAt: new Date(scenario.createdAt),
      updatedAt: new Date(scenario.updatedAt),
    }));

  const acquisitionCostModels: AcquisitionCostModel[] =
    acquisitionCostModelsData.map((model) => ({
      ...model,
      startDate: new Date(model.startDate),
      endDate: model.endDate ? new Date(model.endDate) : undefined,
      createdAt: new Date(model.createdAt),
      updatedAt: new Date(model.updatedAt),
    }));

  const capacityGroups: CustomerCapacityGroup[] = capacityGroupsData.map(
    (group) => ({
      ...group,
      createdAt: new Date(group.createdAt),
      updatedAt: new Date(group.updatedAt),
    }),
  );

  // State hooks - must be declared before any conditional logic
  const [currentSnapshot, setCurrentSnapshot] =
    useState<TimelineSnapshot | null>(null);
  const [zoomLevel, setZoomLevel] = useState<ZoomLevel>(DEFAULT_ZOOM_LEVEL);
  const [zoomCenter, setZoomCenter] = useState<Date | null>(null);
  const [customDateRange, setCustomDateRange] =
    useState<TimelineCustomRange | null>(null);
  const [timelinePrefsReady, setTimelinePrefsReady] = useState(false);
  const [initialCurrentDate, setInitialCurrentDate] = useState<Date | null>(
    null
  );
  const hasSeededTimelineDate = useRef(false);

  // Prevent currentSnapshot from getting reset during data loading
  const [hasInitializedSnapshot, setHasInitializedSnapshot] = useState(false);

  // Store the snapshot at the moment the expense modal is opened
  const [expenseModalSnapshot, setExpenseModalSnapshot] =
    useState<TimelineSnapshot | null>(null);

  // Modal states
  const [isExpenseModalOpen, setIsExpenseModalOpen] = useState(false);
  const [isRevenueModalOpen, setIsRevenueModalOpen] = useState(false);
  const [isEventModalOpen, setIsEventModalOpen] = useState(false);
  const [isCustomerModalOpen, setIsCustomerModalOpen] = useState(false);
  const [isSubscriptionTierModalOpen, setIsSubscriptionTierModalOpen] =
    useState(false);
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [isProPaywallOpen, setIsProPaywallOpen] = useState(false);
  const [isFundingCalculatorOpen, setIsFundingCalculatorOpen] = useState(false);
  const [isCapacityGroupModalOpen, setIsCapacityGroupModalOpen] = useState(false);
  const [editingCapacityGroup, setEditingCapacityGroup] =
    useState<CustomerCapacityGroup | null>(null);
  const [editingExpense, setEditingExpense] = useState<Expense | null>(null);
  const [editingRevenue, setEditingRevenue] = useState<Revenue | null>(null);
  const [editingEvent, setEditingEvent] = useState<FinancialEvent | null>(null);
  const [revisingAsset, setRevisingAsset] = useState<FinancialEvent | null>(null);
  const [isAssetRevisionModalOpen, setIsAssetRevisionModalOpen] = useState(false);
  const [revisingRevenue, setRevisingRevenue] = useState<Revenue | null>(null);
  const [isRevenueRevisionModalOpen, setIsRevenueRevisionModalOpen] = useState(false);
  const [revisingExpense, setRevisingExpense] = useState<Expense | null>(null);
  const [isExpenseRevisionModalOpen, setIsExpenseRevisionModalOpen] = useState(false);
  const [editingCustomerProjection, setEditingCustomerProjection] =
    useState<CustomerProjection | null>(null);
  const [customerProjectionSeed, setCustomerProjectionSeed] =
    useState<Partial<CreateCustomerProjectionData> | null>(null);
  const [editingSubscriptionTier, setEditingSubscriptionTier] =
    useState<SubscriptionTier | null>(null);

  // API mutations - must be declared before conditional logic
  const createExpenseMutation = useCreateExpense(startupId);
  const updateExpenseMutation = useUpdateExpense(startupId);
  const deleteExpenseMutation = useDeleteExpense(startupId);

  const createRevenueMutation = useCreateRevenue(startupId);
  const updateRevenueMutation = useUpdateRevenue(startupId);
  const deleteRevenueMutation = useDeleteRevenue(startupId);

  const createEventMutation = useCreateEvent(startupId);
  const updateEventMutation = useUpdateEvent(startupId);
  const deleteEventMutation = useDeleteEvent(startupId);
  const reviseAssetMutation = useReviseAssetEvent(startupId);
  const reviseRevenueMutation = useReviseRevenue(startupId);
  const reviseExpenseMutation = useReviseExpense(startupId);

  // Customer projection mutations
  const createCustomerMutation = useCreateCustomerProjection(startupId);
  const updateCustomerMutation = useUpdateCustomerProjection(startupId);
  const deleteCustomerMutation = useDeleteCustomerProjection(startupId);

  // Subscription tier mutations
  const createCapacityGroupMutation = useCreateCapacityGroup(startupId);
  const updateCapacityGroupMutation = useUpdateCapacityGroup(startupId);
  const deleteCapacityGroupMutation = useDeleteCapacityGroup(startupId);
  const createSubscriptionTierMutation = useCreateSubscriptionTier(startupId);
  const updateSubscriptionTierMutation = useUpdateSubscriptionTier(startupId);
  const deleteSubscriptionTierMutation = useDeleteSubscriptionTier(startupId);

  // Transform API data to component format
  const data = useMemo(() => {
    if (!startupData) return generateSampleData(); // Fallback to mock data

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const startup = startupData as any;

    return {
      startDate: startup.startDate
        ? parseCalendarDate(startup.startDate) ?? new Date("2024-01-01")
        : startup.foundedDate
          ? parseCalendarDate(startup.foundedDate) ?? new Date("2024-01-01")
          : new Date("2024-01-01"),
      endDate: startup.endDate
        ? parseCalendarDate(startup.endDate) ?? new Date("2028-12-31")
        : new Date("2028-12-31"),
      projectEndDate: startup.endDate
        ? parseCalendarDate(startup.endDate) ?? undefined
        : undefined,
      initialBalance: startup.initialBalance || startup.initialFunding || 0,
      expenses:
        startup.expenses?.map((expense: Expense) => ({
          ...expense,
          isOneTime: Boolean(expense.isOneTime),
          monthlyAmount: expense.isOneTime
            ? 0
            : Number(expense.monthlyAmount ?? expense.amount ?? 0),
          oneTimeAmount: expense.oneTimeAmount
            ? Number(expense.oneTimeAmount)
            : undefined,
          dayOfMonth: Number(expense.dayOfMonth),
          startDate: expense.startDate
            ? parseCalendarDate(expense.startDate) ?? undefined
            : undefined,
          endDate: expense.endDate
            ? parseCalendarDate(expense.endDate) ?? undefined
            : undefined,
          lineItemGroupId: expense.lineItemGroupId,
          segmentKind: expense.segmentKind,
          revisesId: expense.revisesId,
          revisionNote: expense.revisionNote,
        })) || [],
      revenues:
        startup.revenues?.map((revenue: Revenue) => ({
          ...revenue,
          monthlyAmount: Number(revenue.monthlyAmount ?? revenue.amount ?? 0),
          dayOfMonth: Number(revenue.dayOfMonth),
          startDate: revenue.startDate
            ? parseCalendarDate(revenue.startDate) ?? undefined
            : revenue.date
              ? parseCalendarDate(revenue.date) ?? undefined
              : undefined,
          endDate: revenue.endDate
            ? parseCalendarDate(revenue.endDate) ?? undefined
            : undefined,
          lineItemGroupId: revenue.lineItemGroupId,
          segmentKind: revenue.segmentKind,
          revisesId: revenue.revisesId,
          revisionNote: revenue.revisionNote,
          isCustomerDriven: revenue.isCustomerDriven,
        })) || [],
      events:
        startup.events?.map((event: FinancialEvent) => ({
          ...event,
          date: parseCalendarDate(event.date) ?? new Date(event.date),
          endDate: event.endDate
            ? parseCalendarDate(event.endDate) ?? undefined
            : undefined,
        })) || [],
    };
  }, [startupData]);

  // Create customer projection data separately to avoid infinite loops
  const customerProjectionData = useMemo(() => {
    if (
      (customerProjections && customerProjections.length > 0) ||
      (subscriptionTiers && subscriptionTiers.length > 0)
    ) {
      return {
        projections: customerProjections || [],
        subscriptionTiers: subscriptionTiers || [],
        growthScenarios: customerGrowthScenarios || [],
        acquisitionCostModels: acquisitionCostModels || [],
        capacityGroups: capacityGroups || [],
        capacityEvents: buildCapacityEventsFromTimelineEvents(
          ((startupData as { events?: FinancialEvent[] })?.events || []).map(
            (event) => ({
              ...event,
              date: new Date(event.date),
            }),
          ),
        ),
      };
    }
    return undefined;
  }, [
    customerProjections
      ?.map(
        (p) =>
          `${p.id}-${p.churnRateMonthly}-${p.monthlyAcquisitionTarget}-${p.monthlyGrowthRate}-${p.maxCustomers}-${p.maxMonthlyAcquisitions}-${p.monthlyServicingCostPerCustomer}-${p.capacityGroupId}`,
      )
      .join("|"),
    subscriptionTiers
      ?.map(
        (t) =>
          `${t.id}-${t.price}-${t.revenueModel}-${t.pricePerVisit}-${t.avgVisitsPerCustomerPerMonth}`,
      )
      .join("|"),
    customerGrowthScenarios
      ?.map(
        (s) => `${s.id}-${s.baseMonthlyAcquisition}-${s.growthAcceleration}`
      )
      .join("|"),
    acquisitionCostModels
      ?.map((m) => `${m.id}-${m.fixedCostPerCustomer}`)
      .join("|"),
    capacityGroups?.map((g) => `${g.id}-${g.maxCustomers}`).join("|"),
    ((startupData as { events?: FinancialEvent[] })?.events || [])
      .map(
        (e) =>
          `${e.id}-${e.capacityGroupId}-${e.projectionId}-${e.capacityIncrease}`,
      )
      .join("|"),
  ]);

  // Combine data with customer projection data
  const dataWithCustomerProjections = useMemo(
    () => ({
      ...data,
      customerProjectionData,
    }),
    [data, customerProjectionData]
  );

  const visibleDateRange = useMemo(
    () =>
      calculateVisibleDateRange({
        zoomLevel,
        zoomCenter,
        startupStartDate: dataWithCustomerProjections.startDate,
        startupEndDate: dataWithCustomerProjections.endDate,
        customRange: customDateRange,
      }),
    [
      zoomLevel,
      zoomCenter,
      customDateRange,
      dataWithCustomerProjections.startDate,
      dataWithCustomerProjections.endDate,
    ],
  );

  useEffect(() => {
    if (!firebaseUser?.uid || !startupId) {
      setTimelinePrefsReady(false);
      return;
    }

    setTimelinePrefsReady(false);

    const saved = loadTimelineRangePreference(firebaseUser.uid, startupId);
    if (saved) {
      setZoomLevel(saved.zoomLevel);
      setCustomDateRange(saved.customDateRange);
      setZoomCenter(saved.zoomCenter);
    } else {
      setZoomLevel(DEFAULT_ZOOM_LEVEL);
      setCustomDateRange(null);
      setZoomCenter(null);
    }

    setTimelinePrefsReady(true);
  }, [firebaseUser?.uid, startupId]);

  useEffect(() => {
    if (!timelinePrefsReady || !firebaseUser?.uid || !startupId) {
      return;
    }

    saveTimelineRangePreference(firebaseUser.uid, startupId, {
      zoomLevel,
      customDateRange,
      zoomCenter,
    });
  }, [
    timelinePrefsReady,
    firebaseUser?.uid,
    startupId,
    zoomLevel,
    customDateRange,
    zoomCenter,
  ]);

  // All useCallback hooks MUST be declared before any conditional logic
  const handleExpenseClick = useCallback(
    (expenseId: string) => {
      if (expenseId === PROJECTED_CUSTOMER_ACQUISITION_EXPENSE_ID) {
        const projection = customerProjections[0];
        if (projection) {
          setEditingCustomerProjection(projection);
          setIsCustomerModalOpen(true);
        }
        return;
      }

      if (expenseId.startsWith("asset-")) {
        const eventId = expenseId.replace(/^asset-/, "");
        const event = dataWithCustomerProjections.events.find(
          (evt: FinancialEvent) => evt.id === eventId,
        );
        if (event) {
          setEditingEvent(event);
          setIsEventModalOpen(true);
        }
        return;
      }

      const expense = dataWithCustomerProjections.expenses.find(
        (exp: Expense) => exp.id === expenseId,
      );
      if (expense) {
        setEditingExpense(expense);
        setIsExpenseModalOpen(true);
      }
    },
    [
      customerProjections,
      dataWithCustomerProjections.expenses,
      dataWithCustomerProjections.events,
    ],
  );

  const handleRevenueClick = useCallback(
    (revenueId: string) => {
      if (revenueId === "customer-subscription-revenue") {
        const projection = customerProjections[0];
        if (projection) {
          setEditingCustomerProjection(projection);
          setIsCustomerModalOpen(true);
        }
        return;
      }

      if (revenueId.startsWith("asset-")) {
        const eventId = revenueId.replace(/^asset-/, "");
        const event = dataWithCustomerProjections.events.find(
          (evt: FinancialEvent) => evt.id === eventId,
        );
        if (event) {
          setEditingEvent(event);
          setIsEventModalOpen(true);
        }
        return;
      }

      const revenue = dataWithCustomerProjections.revenues?.find(
        (rev: Revenue) => rev.id === revenueId,
      );
      if (revenue) {
        setEditingRevenue(revenue);
        setIsRevenueModalOpen(true);
      }
    },
    [
      customerProjections,
      dataWithCustomerProjections.revenues,
      dataWithCustomerProjections.events,
    ],
  );

  const handleEventClick = useCallback(
    (eventId: string) => {
      // Find the event in the current data
      const event = dataWithCustomerProjections.events.find(
        (evt: FinancialEvent) => evt.id === eventId
      );
      if (event) {
        setEditingEvent(event);
        setIsEventModalOpen(true);
      }
    },
    [dataWithCustomerProjections.events]
  );

  const handleCustomerProjectionClick = useCallback(
    (projectionId: string) => {
      // Find the customer projection in the current data
      const projection = customerProjections.find(
        (proj: CustomerProjection) => proj.id === projectionId
      );
      if (projection) {
        setEditingCustomerProjection(projection);
        setIsCustomerModalOpen(true);
      }
    },
    [customerProjections]
  );

  const handleSubscriptionTierClick = useCallback(
    (tierId: string) => {
      // Find the subscription tier in the current data
      const tier = subscriptionTiers.find(
        (t: SubscriptionTier) => t.id === tierId
      );
      if (tier) {
        setEditingSubscriptionTier(tier);
        setIsSubscriptionTierModalOpen(true);
      }
    },
    [subscriptionTiers]
  );

  const openCapacityGroupModal = useCallback((group?: CustomerCapacityGroup | null) => {
    setEditingCapacityGroup(group ?? null);
    setIsCapacityGroupModalOpen(true);
  }, []);

  const handleCapacityGroupClick = useCallback(
    (groupId: string) => {
      const group = capacityGroups.find(
        (entry: CustomerCapacityGroup) => entry.id === groupId,
      );
      if (group) {
        openCapacityGroupModal(group);
      }
    },
    [capacityGroups, openCapacityGroupModal],
  );

  // Preserve currentSnapshot across re-renders by using a ref
  const preservedSnapshot = useRef<TimelineSnapshot | null>(null);
  // Use Jotai atom for timeline snapshot - survives React re-renders completely
  const [latestTimelineSnapshot, setLatestTimelineSnapshot] =
    useAtom(timelineSnapshotAtom);

  // Initialize atom once with current snapshot
  useEffect(() => {
    if (!latestTimelineSnapshot && currentSnapshot) {
      setLatestTimelineSnapshot(currentSnapshot);
      console.log("Jotai atom initialized:", currentSnapshot.date);
    }
  }, [currentSnapshot, latestTimelineSnapshot, setLatestTimelineSnapshot]);

  const handleTimeChange = useCallback(
    (snapshot: TimelineSnapshot) => {
      console.log("handleTimeChange called with snapshot date:", snapshot.date);
      // Store in Jotai atom - completely bypasses React state re-render issues
      setLatestTimelineSnapshot(snapshot);
      console.log("Jotai atom updated to:", snapshot.date);
      setCurrentSnapshot(snapshot);
      setHasInitializedSnapshot(true);
    },
    [setLatestTimelineSnapshot]
  );

  const handleMobileDateChange = useCallback(
    (date: Date) => {
      const clamped = clampDateToRange(date, visibleDateRange);
      const snapshot = calculateBurndownSnapshot(dataWithCustomerProjections, clamped);
      setLatestTimelineSnapshot(snapshot);
      setCurrentSnapshot(snapshot);
      setHasInitializedSnapshot(true);
    },
    [dataWithCustomerProjections, visibleDateRange, setLatestTimelineSnapshot],
  );

  // Update preserved snapshot whenever currentSnapshot changes
  useEffect(() => {
    if (currentSnapshot && hasInitializedSnapshot) {
      // Only preserve if we have a user-initiated snapshot (not initial load)
      preservedSnapshot.current = currentSnapshot;
      // DON'T update latestTimelineSnapshot here - it gets updated directly in handleTimeChange
      // console.log(
      // "Preserved timeline date:",
      // currentSnapshot.date.toLocaleDateString(),
      // );
    }
  }, [currentSnapshot, hasInitializedSnapshot]);

  const handleZoomChange = useCallback(
    (newZoomLevel: ZoomLevel) => {
      setZoomLevel(newZoomLevel);
      if (newZoomLevel === "custom") {
        setCustomDateRange((prev) =>
          prev ?? buildDefaultCustomRange(dataWithCustomerProjections.startDate),
        );
        setZoomCenter(null);
      }
    },
    [dataWithCustomerProjections.startDate],
  );

  const handlePanLeft = useCallback(() => {
    if (zoomLevel === "custom" && customDateRange) {
      setCustomDateRange(panCustomRange(customDateRange, "left"));
      return;
    }
    const currentDate = zoomCenter || dataWithCustomerProjections.startDate;
    setZoomCenter(panDateByZoom(currentDate, zoomLevel, "left"));
  }, [
    zoomLevel,
    zoomCenter,
    customDateRange,
    dataWithCustomerProjections.startDate,
  ]);

  const handlePanRight = useCallback(() => {
    if (zoomLevel === "custom" && customDateRange) {
      setCustomDateRange(panCustomRange(customDateRange, "right"));
      return;
    }
    const currentDate = zoomCenter || dataWithCustomerProjections.startDate;
    setZoomCenter(panDateByZoom(currentDate, zoomLevel, "right"));
  }, [
    zoomLevel,
    zoomCenter,
    customDateRange,
    dataWithCustomerProjections.startDate,
  ]);

  const handleGoToToday = useCallback(() => {
    setZoomCenter(null);
    setInitialCurrentDate(new Date());
  }, []);

  const handleGoToStart = useCallback(() => {
    setInitialCurrentDate(null);
    setZoomCenter(new Date(dataWithCustomerProjections.startDate));
  }, [dataWithCustomerProjections.startDate]);

  const handleGoToEnd = useCallback(() => {
    const end =
      dataWithCustomerProjections.endDate || dataWithCustomerProjections.startDate;
    setInitialCurrentDate(null);
    setZoomCenter(new Date(end));
  }, [dataWithCustomerProjections.endDate, dataWithCustomerProjections.startDate]);

  const openAddExpense = useCallback(() => {
    setEditingExpense(null);
    const snapshotToUse = latestTimelineSnapshot || currentSnapshot;
    setExpenseModalSnapshot(snapshotToUse ?? null);
    setIsExpenseModalOpen(true);
  }, [latestTimelineSnapshot, currentSnapshot]);

  const openAddRevenue = useCallback(() => {
    setEditingRevenue(null);
    setIsRevenueModalOpen(true);
  }, []);

  const openAddEvent = useCallback(() => {
    setEditingEvent(null);
    setIsEventModalOpen(true);
  }, []);

  const openAddCustomerProjection = useCallback(() => {
    setEditingCustomerProjection(null);
    setIsCustomerModalOpen(true);
  }, []);

  const handleIncorporateGrowthPlan = useCallback(
    (params: {
      monthlyGrowthRatePercent: number;
      monthlyRamp: number;
      projectionType: "growth_rate";
    }) => {
      setCustomerProjectionSeed({
        projectionType: params.projectionType,
        monthlyGrowthRate: params.monthlyGrowthRatePercent / 100,
        name: "Growth plan projection",
      });
      setEditingCustomerProjection(null);
      setIsCustomerModalOpen(true);
    },
    [],
  );

  const quickAddActions = useMemo(
    () => [
      {
        id: "expense",
        label: "Add expense",
        icon: DollarSign,
        tone: "expense" as const,
        onClick: openAddExpense,
      },
      {
        id: "revenue",
        label: "Add revenue",
        icon: TrendingUp,
        tone: "revenue" as const,
        onClick: openAddRevenue,
      },
      {
        id: "event",
        label: "Add event",
        icon: Calendar,
        tone: "focus" as const,
        onClick: openAddEvent,
      },
      {
        id: "customer",
        label: "Customer projection",
        icon: Users,
        tone: "focus" as const,
        onClick: () => {
          setEditingCustomerProjection(null);
          setIsCustomerModalOpen(true);
        },
      },
      {
        id: "tier",
        label: "Revenue plan",
        icon: Package,
        tone: "neutral" as const,
        onClick: () => {
          setEditingSubscriptionTier(null);
          setIsSubscriptionTierModalOpen(true);
        },
      },
      {
        id: "bulk-shift",
        label: "Bulk date shift",
        icon: CalendarClock,
        tone: "neutral" as const,
        onClick: () => setIsBulkShiftOpen(true),
      },
      {
        id: "funding-calculator",
        label: "Funding calculator",
        icon: Calculator,
        tone: "brand" as const,
        onClick: () => setIsFundingCalculatorOpen(true),
      },
      {
        id: "capacity-group",
        label: "Capacity group",
        icon: Users,
        tone: "neutral" as const,
        onClick: () => openCapacityGroupModal(null),
      },
      {
        id: "chat",
        label: "AI advisor",
        icon: MessageCircle,
        tone: "brand" as const,
        onClick: () => {
          if (isPro) {
            setIsChatOpen(true);
          } else {
            setIsProPaywallOpen(true);
          }
        },
      },
    ],
    [openAddExpense, openAddRevenue, openAddEvent, openCapacityGroupModal, isPro],
  );

  const rawExpenses = ((startupData as { expenses?: Expense[] })?.expenses ||
    []) as Expense[];
  const rawRevenues = ((startupData as { revenues?: Revenue[] })?.revenues ||
    []) as Revenue[];
  const rawEvents = ((startupData as { events?: FinancialEvent[] })?.events ||
    []) as FinancialEvent[];

  const showSetupHero =
    rawExpenses.length === 0 &&
    rawRevenues.length === 0 &&
    rawEvents.length === 0;

  const handleBulkExpenseUpdates = useCallback(
    async (updates: Array<{ id: string; startDate?: Date; endDate?: Date }>) => {
      await Promise.all(
        updates.map((update) =>
          updateExpenseMutation.mutateAsync({
            expenseId: update.id,
            data: {
              startDate: update.startDate?.toISOString(),
              endDate: update.endDate?.toISOString(),
            },
          }),
        ),
      );
    },
    [updateExpenseMutation],
  );

  const handleBulkRevenueUpdates = useCallback(
    async (
      updates: Array<{ id: string; date?: Date; startDate?: Date; endDate?: Date }>,
    ) => {
      await Promise.all(
        updates.map((update) =>
          updateRevenueMutation.mutateAsync({
            revenueId: update.id,
            data: {
              startDate: (update.date ?? update.startDate)?.toISOString(),
              endDate: update.endDate?.toISOString(),
            },
          }),
        ),
      );
    },
    [updateRevenueMutation],
  );

  // Seed scrubber to today once per startup (skip when restoring a saved pan position)
  useEffect(() => {
    hasSeededTimelineDate.current = false;
  }, [startupId]);

  useEffect(() => {
    if (!startupData || !timelinePrefsReady || hasSeededTimelineDate.current) {
      return;
    }

    hasSeededTimelineDate.current = true;

    if (!zoomCenter) {
      setInitialCurrentDate(new Date());
    }
  }, [startupData, timelinePrefsReady, zoomCenter]);

  // Handle form submissions
  const handleExpenseSubmit = useCallback(
    async (data: CreateExpenseData) => {
      try {
        if (
          editingExpense &&
          hasLaterSegments(
            editingExpense,
            dataWithCustomerProjections.expenses,
          ) &&
          !confirmEditSupersededSegment(editingExpense.name)
        ) {
          return;
        }
        console.log("BurndownChartDemo: Expense data received:", data);
        if (editingExpense) {
          // Update existing expense
          console.log(
            "BurndownChartDemo: Updating expense with ID:",
            editingExpense.id
          );
          await updateExpenseMutation.mutateAsync({
            expenseId: editingExpense.id,
            data,
          });
        } else {
          // Create new expense
          console.log("BurndownChartDemo: Creating new expense");
          await createExpenseMutation.mutateAsync(data);
        }
        setIsExpenseModalOpen(false);
        setEditingExpense(null);
        setExpenseModalSnapshot(null); // Clear the captured snapshot
        // Query invalidation happens automatically via the hook's onSuccess
      } catch (error) {
        console.error("Failed to save expense:", error);
        // Keep modal open on error so user can try again
      }
    },
    [createExpenseMutation, updateExpenseMutation, editingExpense, dataWithCustomerProjections.expenses]
  );

  const handleExpenseDelete = useCallback(
    async (expenseId: string) => {
      if (!window.confirm("Are you sure you want to delete this expense?")) {
        return;
      }

      try {
        await deleteExpenseMutation.mutateAsync(expenseId);
        setIsExpenseModalOpen(false);
        setEditingExpense(null);
      } catch (error) {
        console.error("Failed to delete expense:", error);
      }
    },
    [deleteExpenseMutation]
  );

  const handleRevenueSubmit = useCallback(
    async (data: CreateRevenueData) => {
      try {
        if (
          editingRevenue &&
          hasLaterSegments(
            editingRevenue,
            dataWithCustomerProjections.revenues,
          ) &&
          !confirmEditSupersededSegment(editingRevenue.name)
        ) {
          return;
        }
        if (editingRevenue) {
          // Update existing revenue
          await updateRevenueMutation.mutateAsync({
            revenueId: editingRevenue.id,
            data,
          });
        } else {
          // Create new revenue
          await createRevenueMutation.mutateAsync(data);
        }
        setIsRevenueModalOpen(false);
        setEditingRevenue(null);
      } catch (error) {
        console.error("Failed to save revenue:", error);
      }
    },
    [createRevenueMutation, updateRevenueMutation, editingRevenue, dataWithCustomerProjections.revenues]
  );

  const handleRevenueDelete = useCallback(
    async (revenueId: string) => {
      if (!window.confirm("Are you sure you want to delete this revenue?")) {
        return;
      }

      try {
        await deleteRevenueMutation.mutateAsync(revenueId);
        setIsRevenueModalOpen(false);
        setEditingRevenue(null);
      } catch (error) {
        console.error("Failed to delete revenue:", error);
      }
    },
    [deleteRevenueMutation]
  );

  const handleEventSubmit = useCallback(
    async (data: CreateEventData) => {
      try {
        if (
          editingEvent &&
          editingEvent.type === "asset" &&
          hasLaterSegments(
            { ...editingEvent, startDate: editingEvent.date },
            dataWithCustomerProjections.events.map((event: FinancialEvent) => ({
              ...event,
              startDate: event.date,
            })),
          ) &&
          !confirmEditSupersededSegment(
            editingEvent.description || editingEvent.title || "Asset",
          )
        ) {
          return;
        }
        if (editingEvent) {
          // Update existing event
          await updateEventMutation.mutateAsync({
            eventId: editingEvent.id,
            data,
          });
        } else {
          // Create new event
          await createEventMutation.mutateAsync(data);
        }
        setIsEventModalOpen(false);
        setEditingEvent(null);
      } catch (error) {
        console.error("Failed to save event:", error);
      }
    },
    [createEventMutation, updateEventMutation, editingEvent, dataWithCustomerProjections.events]
  );

  const handleEventDelete = useCallback(
    async (eventId: string) => {
      if (!window.confirm("Are you sure you want to delete this event?")) {
        return;
      }

      try {
        await deleteEventMutation.mutateAsync(eventId);
        setIsEventModalOpen(false);
        setEditingEvent(null);
      } catch (error) {
        console.error("Failed to delete event:", error);
      }
    },
    [deleteEventMutation]
  );

  const openAssetRevision = useCallback(() => {
    if (!editingEvent) return;
    const target = getOpenAssetSegmentTarget(
      editingEvent,
      dataWithCustomerProjections.events,
    );
    if (!target) return;
    setRevisingAsset(target);
    setIsEventModalOpen(false);
    setEditingEvent(null);
    setIsAssetRevisionModalOpen(true);
  }, [editingEvent, dataWithCustomerProjections.events]);

  const handleAssetRevisionSubmit = useCallback(
    async (data: ReviseAssetEventData) => {
      if (!revisingAsset) return;
      try {
        await reviseAssetMutation.mutateAsync({
          eventId: revisingAsset.id,
          data,
        });
        setIsAssetRevisionModalOpen(false);
        setRevisingAsset(null);
      } catch (error) {
        console.error("Failed to revise asset:", error);
      }
    },
    [reviseAssetMutation, revisingAsset],
  );

  const reviseTargetForEditingEvent = useMemo(() => {
    if (!editingEvent) return null;
    return getOpenAssetSegmentTarget(
      editingEvent,
      dataWithCustomerProjections.events,
    );
  }, [editingEvent, dataWithCustomerProjections.events]);

  const openRevenueRevision = useCallback(() => {
    if (!editingRevenue) return;
    const target = getOpenSegmentTarget(
      editingRevenue,
      dataWithCustomerProjections.revenues,
    );
    if (!target) return;
    setRevisingRevenue(target);
    setIsRevenueModalOpen(false);
    setEditingRevenue(null);
    setIsRevenueRevisionModalOpen(true);
  }, [editingRevenue, dataWithCustomerProjections.revenues]);

  const handleRevenueRevisionSubmit = useCallback(
    async (data: ReviseRevenueData) => {
      if (!revisingRevenue) return;
      try {
        await reviseRevenueMutation.mutateAsync({
          revenueId: revisingRevenue.id,
          data,
        });
        setIsRevenueRevisionModalOpen(false);
        setRevisingRevenue(null);
      } catch (error) {
        console.error("Failed to revise revenue:", error);
      }
    },
    [reviseRevenueMutation, revisingRevenue],
  );

  const canReviseEditingRevenue = useMemo(() => {
    if (!editingRevenue || editingRevenue.isCustomerDriven) return false;
    if (editingRevenue.isRecurring === false) return false;
    if (editingRevenue.frequency === "one-time") return false;
    return canReviseSegment(
      editingRevenue,
      dataWithCustomerProjections.revenues,
    );
  }, [editingRevenue, dataWithCustomerProjections.revenues]);

  const openExpenseRevision = useCallback(() => {
    if (!editingExpense) return;
    const target = getOpenSegmentTarget(
      editingExpense,
      dataWithCustomerProjections.expenses,
    );
    if (!target) return;
    setRevisingExpense(target);
    setIsExpenseModalOpen(false);
    setEditingExpense(null);
    setExpenseModalSnapshot(null);
    setIsExpenseRevisionModalOpen(true);
  }, [editingExpense, dataWithCustomerProjections.expenses]);

  const handleExpenseRevisionSubmit = useCallback(
    async (data: ReviseExpenseData) => {
      if (!revisingExpense) return;
      try {
        await reviseExpenseMutation.mutateAsync({
          expenseId: revisingExpense.id,
          data,
        });
        setIsExpenseRevisionModalOpen(false);
        setRevisingExpense(null);
      } catch (error) {
        console.error("Failed to revise expense:", error);
      }
    },
    [reviseExpenseMutation, revisingExpense],
  );

  const canReviseEditingExpense = useMemo(() => {
    if (!editingExpense || editingExpense.isOneTime) return false;
    if (editingExpense.frequency === "one-time") return false;
    return canReviseSegment(
      editingExpense,
      dataWithCustomerProjections.expenses,
    );
  }, [editingExpense, dataWithCustomerProjections.expenses]);

  const handleRevisionReviseAgain = useCallback(
    (payload: {
      kind: RevisionItemKind;
      segmentId: string;
      groupId: string;
    }) => {
      if (payload.kind === "expense") {
        const expense = dataWithCustomerProjections.expenses.find(
          (item: Expense) => item.id === payload.segmentId,
        );
        if (!expense) return;
        setRevisingExpense(expense);
        setIsExpenseRevisionModalOpen(true);
        return;
      }

      if (payload.kind === "revenue") {
        const revenue = dataWithCustomerProjections.revenues.find(
          (item: Revenue) => item.id === payload.segmentId,
        );
        if (!revenue) return;
        setRevisingRevenue(revenue);
        setIsRevenueRevisionModalOpen(true);
        return;
      }

      const asset = dataWithCustomerProjections.events.find(
        (item: FinancialEvent) => item.id === payload.segmentId,
      );
      if (!asset) return;
      setRevisingAsset(asset);
      setIsAssetRevisionModalOpen(true);
    },
    [dataWithCustomerProjections],
  );

  // Customer projection handlers
  const handleCustomerSubmit = useCallback(
    async (data: any) => {
      try {
        if (editingCustomerProjection) {
          await updateCustomerMutation.mutateAsync({
            projectionId: editingCustomerProjection.id,
            data,
          });
        } else {
          await createCustomerMutation.mutateAsync(data);
        }
        setIsCustomerModalOpen(false);
        setEditingCustomerProjection(null);
        setCustomerProjectionSeed(null);
      } catch (error) {
        console.error("Failed to save customer projection:", error);
      }
    },
    [createCustomerMutation, updateCustomerMutation, editingCustomerProjection]
  );

  const handleCustomerDelete = useCallback(
    async (projectionId: string) => {
      if (
        !window.confirm(
          "Are you sure you want to delete this customer projection?"
        )
      ) {
        return;
      }

      try {
        await deleteCustomerMutation.mutateAsync(projectionId);
        setIsCustomerModalOpen(false);
        setEditingCustomerProjection(null);
      } catch (error) {
        console.error("Failed to delete customer projection:", error);
      }
    },
    [deleteCustomerMutation]
  );

  // Subscription tier handlers
  const handleSubscriptionTierSubmit = useCallback(
    async (data: any) => {
      try {
        if (editingSubscriptionTier) {
          await updateSubscriptionTierMutation.mutateAsync({
            tierId: editingSubscriptionTier.id,
            data,
          });
        } else {
          await createSubscriptionTierMutation.mutateAsync(data);
        }
        setIsSubscriptionTierModalOpen(false);
        setEditingSubscriptionTier(null);
      } catch (error) {
        console.error("Failed to save subscription tier:", error);
      }
    },
    [
      createSubscriptionTierMutation,
      updateSubscriptionTierMutation,
      editingSubscriptionTier,
    ]
  );

  const handleSubscriptionTierDelete = useCallback(
    async (tierId: string) => {
      if (
        !window.confirm(
          "Are you sure you want to delete this subscription tier?"
        )
      ) {
        return;
      }

      try {
        await deleteSubscriptionTierMutation.mutateAsync(tierId);
        setIsSubscriptionTierModalOpen(false);
        setEditingSubscriptionTier(null);
      } catch (error) {
        console.error("Failed to delete subscription tier:", error);
      }
    },
    [deleteSubscriptionTierMutation]
  );

  const handleCapacityGroupSubmit = useCallback(
    async (data: CreateCustomerCapacityGroupData) => {
      try {
        if (editingCapacityGroup) {
          await updateCapacityGroupMutation.mutateAsync({
            groupId: editingCapacityGroup.id,
            data,
          });
        } else {
          await createCapacityGroupMutation.mutateAsync({
            ...data,
            startupId,
          });
        }
        setIsCapacityGroupModalOpen(false);
        setEditingCapacityGroup(null);
      } catch (error) {
        console.error("Failed to save capacity group:", error);
      }
    },
    [
      createCapacityGroupMutation,
      updateCapacityGroupMutation,
      editingCapacityGroup,
      startupId,
    ],
  );

  const handleCapacityGroupDelete = useCallback(
    async (groupId: string) => {
      const linkedCount = customerProjections.filter(
        (projection) => projection.capacityGroupId === groupId,
      ).length;
      const message =
        linkedCount > 0
          ? `This capacity group is linked to ${linkedCount} customer projection${linkedCount === 1 ? "" : "s"}. Delete it anyway? Projections will keep their other settings but lose the shared capacity link.`
          : "Are you sure you want to delete this capacity group?";

      if (!window.confirm(message)) {
        return;
      }

      try {
        await deleteCapacityGroupMutation.mutateAsync(groupId);
        setIsCapacityGroupModalOpen(false);
        setEditingCapacityGroup(null);
      } catch (error) {
        console.error("Failed to delete capacity group:", error);
      }
    },
    [customerProjections, deleteCapacityGroupMutation],
  );

  // Show loading state if we're still loading startups or startup data
  if (startupsLoading || (startupId && isLoading)) {
    return (
      <div className="flex min-h-[400px] w-full items-center justify-center p-6">
        <div className="h-12 w-12 animate-spin rounded-full border-2 border-brand border-t-transparent" />
      </div>
    );
  }

  // Show error state if no startup is selected
  if (!currentStartup) {
    return (
      <div className="w-full p-6">
        <div className="rounded-[20px] border border-warn/30 bg-warn/10 p-6 text-center">
          <h3 className="text-warn font-semibold">No startup selected</h3>
          <p className="text-warn/90 text-sm mt-2 max-w-md mx-auto">
            Create your first startup or select one from the header to view
            financial data. Each startup keeps its finances completely separate.
          </p>
          <button
            type="button"
            onClick={openCreateStartupModal}
            className="mt-4 inline-flex rounded-[14px] bg-brand px-4 py-2 text-sm font-semibold text-surface"
          >
            Create startup
          </button>
        </div>
      </div>
    );
  }

  return (
    <>
      {!isPro ? <ProUpgradeStrip /> : null}
      {/* Mobile Layout - Only shown on mobile screens */}
      <div className="lg:hidden min-h-screen bg-page">
        {/* Mobile Financial Summary */}
        <MobileFinancialSummary
          currentSnapshot={currentSnapshot}
          customerProjectionData={customerProjectionData}
          currentDate={currentSnapshot?.date}
        />

        {/* Mobile Timeline Controls */}
        <MobileTimelineControls
          zoomLevel={zoomLevel}
          onZoomChange={handleZoomChange}
          customRange={customDateRange}
          onCustomRangeChange={setCustomDateRange}
          onPanLeft={handlePanLeft}
          onPanRight={handlePanRight}
          onGoToToday={handleGoToToday}
          onGoToStart={handleGoToStart}
          onGoToEnd={handleGoToEnd}
        />

        {showSetupHero ? (
          <div className="px-4 pt-4">
            <StartupSetupHero
              startupName={currentStartup.name}
              hasStartDate={Boolean((startupData as { startDate?: string })?.startDate)}
              hasInitialFunding={
                ((startupData as { initialFunding?: number })?.initialFunding || 0) > 0
              }
              expenseCount={rawExpenses.length}
              onSetTimeline={() => navigate("/settings/timeline")}
              onAddFunding={openAddRevenue}
              onAddExpense={openAddExpense}
              onOpenSettings={() => navigate("/settings/startup")}
            />
          </div>
        ) : null}

        <div className="px-4 py-3">
          <QuickAddBar actions={quickAddActions} />
        </div>

        {/* Mobile Timeline Visualization */}
        <MobileBurndownChart
          data={dataWithCustomerProjections}
          currentDate={
            currentSnapshot?.date ??
            getDefaultTimelineDate(
              dataWithCustomerProjections.startDate,
              dataWithCustomerProjections.endDate,
            )
          }
          visibleDateRange={visibleDateRange}
          rawEvents={(startupData as { events?: FinancialEvent[] })?.events || []}
          onDateChange={handleMobileDateChange}
        />

        {/* Mobile Charts */}
        <MobileCharts
          data={dataWithCustomerProjections}
          visibleDateRange={visibleDateRange}
          events={(startupData as any)?.events || []}
          currentSnapshot={currentSnapshot}
        />

        {/* Mobile Action Buttons */}
        <MobileActionButtons
          startupId={startupId}
          onAddExpense={openAddExpense}
          onAddRevenue={openAddRevenue}
          onAddEvent={openAddEvent}
        />

      </div>

      {/* Desktop Layout - Hidden on mobile screens */}
      <div className="hidden lg:block w-full px-4 py-6 sm:px-6 lg:px-8">
        <div className="mb-6">
          <div className="mb-4 flex items-center justify-between">
            <div>
              <p className="sh-kicker">Runway timeline</p>
              <h1 className="mt-1 text-2xl font-black text-white">
                {/* eslint-disable-next-line @typescript-eslint/no-explicit-any */}
                {(startupData as any)?.name || "Interactive financial timeline"}
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-white/65">
                Scrub through time to see cash, burn, and milestones at any point.
              </p>
            </div>
            <TimelineZoom
              zoomLevel={zoomLevel}
              onZoomChange={handleZoomChange}
              customRange={customDateRange}
              onCustomRangeChange={setCustomDateRange}
              onPanLeft={handlePanLeft}
              onPanRight={handlePanRight}
              onGoToToday={handleGoToToday}
              onGoToStart={handleGoToStart}
              onGoToEnd={handleGoToEnd}
              className="flex-shrink-0"
            />
          </div>

          {showSetupHero ? (
            <StartupSetupHero
              startupName={currentStartup.name}
              hasStartDate={Boolean((startupData as { startDate?: string })?.startDate)}
              hasInitialFunding={
                ((startupData as { initialFunding?: number })?.initialFunding || 0) > 0
              }
              expenseCount={rawExpenses.length}
              onSetTimeline={() => navigate("/settings/timeline")}
              onAddFunding={openAddRevenue}
              onAddExpense={openAddExpense}
              onOpenSettings={() => navigate("/settings/startup")}
            />
          ) : null}

          <div className="mb-4">
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-white/45">
              Quick actions
            </p>
            <QuickAddBar actions={quickAddActions} />
          </div>
        </div>

        <div className="w-full overflow-hidden rounded-[20px] border border-white/[0.09] bg-surface">
          <BurndownChart
            data={dataWithCustomerProjections}
            onExpenseClick={handleExpenseClick}
            onRevenueClick={handleRevenueClick}
            onEventClick={handleEventClick}
            onCustomerProjectionClick={handleCustomerProjectionClick}
            onSubscriptionTierClick={handleSubscriptionTierClick}
            onCapacityGroupClick={handleCapacityGroupClick}
            onAddCapacityGroup={() => openCapacityGroupModal(null)}
            onAddExpense={() => {
              setEditingExpense(null);
              const snapshotToUse = latestTimelineSnapshot || currentSnapshot;
              if (snapshotToUse) {
                setExpenseModalSnapshot(snapshotToUse);
              } else {
                setExpenseModalSnapshot(null);
              }
              setIsExpenseModalOpen(true);
            }}
            onAddRevenue={() => {
              setEditingRevenue(null);
              setIsRevenueModalOpen(true);
            }}
            onAddEvent={() => {
              setEditingEvent(null);
              setIsEventModalOpen(true);
            }}
            onIncorporateGrowthPlan={handleIncorporateGrowthPlan}
            onAddCustomerProjection={openAddCustomerProjection}
            onRevisionReviseAgain={handleRevisionReviseAgain}
            onTimeChange={handleTimeChange}
            zoomLevel={zoomLevel}
            zoomCenter={zoomCenter}
            customDateRange={customDateRange}
            initialCurrentDate={initialCurrentDate}
            rawExpenses={(startupData as any)?.expenses || []}
            rawRevenues={(startupData as any)?.revenues || []}
            rawEvents={(startupData as any)?.events || []}
            customers={customerProjections}
            subscriptionTiers={subscriptionTiers}
            customerGrowthModels={customerGrowthScenarios}
            customerAcquisitionCosts={acquisitionCostModels}
            startupId={(startupData as any)?.id || startupId}
          />
        </div>

        <div className="fixed bottom-6 right-6 z-40 hidden flex-col items-end gap-3 lg:flex">
          {isFloatingMenuExpanded ? (
            <div className="mb-3 flex flex-col items-end gap-3">
              {quickAddActions.map((action) => (
                <LabeledFabAction
                  key={action.id}
                  label={action.label}
                  icon={action.icon}
                  tone={action.tone}
                  badge={action.id === "chat" && !isPro ? "Pro" : undefined}
                  onClick={() => {
                    action.onClick();
                    setIsFloatingMenuExpanded(false);
                  }}
                />
              ))}
            </div>
          ) : null}

          <button
            type="button"
            onClick={() => setIsFloatingMenuExpanded(!isFloatingMenuExpanded)}
            className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full bg-brand text-surface shadow-glow ring-2 ring-brand/30 transition-colors hover:bg-brand-muted"
            title={isFloatingMenuExpanded ? "Close menu" : "Add item"}
            aria-expanded={isFloatingMenuExpanded}
          >
            {isFloatingMenuExpanded ? (
              <X className="h-6 w-6" />
            ) : (
              <Plus className="h-6 w-6" />
            )}
          </button>
        </div>
      </div>

      {/* Chat Interface - Available on desktop */}
      <ChatInterface
        startupId={startupId}
        isOpen={isChatOpen && isPro}
        onClose={() => setIsChatOpen(false)}
      />

      <ProUpgradeModal
        isOpen={isProPaywallOpen}
        onClose={() => setIsProPaywallOpen(false)}
        featureId="ai_advisor"
        showFeatureGrid={false}
      />

      <AskCalculatorModal
        isOpen={isFundingCalculatorOpen}
        onClose={() => setIsFundingCalculatorOpen(false)}
        data={dataWithCustomerProjections}
        currentCash={currentSnapshot?.cashBalance || 0}
        analysisDate={currentSnapshot?.date}
      />

      {/* Modals - Shared between mobile and desktop */}
      {/* Expense Modal */}
      <Modal
        isOpen={isExpenseModalOpen}
        onClose={() => {
          setIsExpenseModalOpen(false);
          setEditingExpense(null);
          setExpenseModalSnapshot(null); // Clear the captured snapshot
        }}
        title={editingExpense ? "Edit Expense" : "Add New Expense"}
      >
        <ExpenseForm
          key={
            editingExpense
              ? `edit-${editingExpense.id}`
              : `new-${currentSnapshot?.date.getTime() || Date.now()}`
          }
          onSubmit={handleExpenseSubmit}
          onCancel={() => {
            setIsExpenseModalOpen(false);
            setEditingExpense(null);
            setExpenseModalSnapshot(null); // Clear the captured snapshot
          }}
          onDelete={
            editingExpense
              ? () => handleExpenseDelete(editingExpense.id)
              : undefined
          }
          initialData={
            editingExpense
              ? {
                  name: editingExpense.name,
                  category: editingExpense.category,
                  monthlyAmount:
                    editingExpense.monthlyAmount || editingExpense.amount || 0,
                  startDate: editingExpense.startDate
                    ? toDateInputValue(editingExpense.startDate)
                    : toDateInputValue(new Date()),
                  endDate: editingExpense.endDate
                    ? toDateInputValue(editingExpense.endDate)
                    : undefined,
                  dayOfMonth: editingExpense.dayOfMonth || 1,
                  color: editingExpense.color || "#000000",
                  // New scaling fields
                  expenseType: editingExpense.expenseType || "fixed",
                  growthRate: editingExpense.growthRate,
                  revenueRatio: editingExpense.revenueRatio,
                  // Customer-based expense fields
                  customerBaseCostPerCustomer:
                    editingExpense.customerBaseCostPerCustomer,
                  customerBaseAmount: editingExpense.customerBaseAmount,
                  // One-time expense fields
                  isOneTime: editingExpense.isOneTime || false,
                  oneTimeAmount: editingExpense.oneTimeAmount || 0,
                }
              : expenseModalSnapshot
              ? (() => {
                  const effectiveSnapshot = expenseModalSnapshot;
                  // Format date properly to avoid timezone issues
                  const snapshotDate = safeDate(effectiveSnapshot.date);
                  const year = snapshotDate.getFullYear();
                  const month = String(snapshotDate.getMonth() + 1).padStart(
                    2,
                    "0"
                  );
                  const day = String(snapshotDate.getDate()).padStart(2, "0");
                  const formattedDate = `${year}-${month}-${day}`;

                  const initialData = {
                    startDate: formattedDate,
                  };
                  console.log("Using captured snapshot for expense form:", {
                    date: effectiveSnapshot.date,
                    formattedDate: initialData.startDate,
                  });
                  return initialData;
                })()
              : (() => {
                  const fallbackData = {
                    startDate: new Date().toISOString().split("T")[0],
                  };
                  // console.log(
                  // "Using fallback date for expense form:",
                  // fallbackData,
                  // );
                  return fallbackData;
                })()
          }
          isLoading={
            createExpenseMutation.isPending ||
            updateExpenseMutation.isPending ||
            deleteExpenseMutation.isPending
          }
          isEditing={!!editingExpense}
          onRevise={openExpenseRevision}
          canRevise={canReviseEditingExpense}
          startupStartDate={dataWithCustomerProjections.startDate}
        />
      </Modal>

      {/* Revenue Modal */}
      {isRevenueModalOpen && (
        <Modal
          isOpen={isRevenueModalOpen}
          onClose={() => {
            setIsRevenueModalOpen(false);
            setEditingRevenue(null);
          }}
          title={editingRevenue ? "Edit Revenue" : "Add Revenue"}
        >
          <RevenueForm
            initialData={
              editingRevenue
                ? {
                    ...editingRevenue,
                    frequency:
                      editingRevenue.frequency === "yearly"
                        ? "annual"
                        : editingRevenue.frequency,
                    startDate: editingRevenue.startDate
                      ? editingRevenue.startDate instanceof Date
                        ? editingRevenue.startDate.toISOString().split("T")[0]
                        : String(editingRevenue.startDate).split("T")[0]
                      : new Date().toISOString().split("T")[0],
                    endDate: editingRevenue.endDate
                      ? editingRevenue.endDate instanceof Date
                        ? editingRevenue.endDate.toISOString().split("T")[0]
                        : String(editingRevenue.endDate).split("T")[0]
                      : undefined,
                  }
                : latestTimelineSnapshot
                ? ({
                    startDate: latestTimelineSnapshot.date
                      .toISOString()
                      .split("T")[0],
                  } as any)
                : currentSnapshot
                ? ({
                    startDate: currentSnapshot.date.toISOString().split("T")[0],
                  } as any)
                : undefined
            }
            onSubmit={handleRevenueSubmit}
            onDelete={
              editingRevenue
                ? () => handleRevenueDelete(editingRevenue.id)
                : undefined
            }
            onCancel={() => {
              setIsRevenueModalOpen(false);
              setEditingRevenue(null);
            }}
            onRevise={openRevenueRevision}
            canRevise={canReviseEditingRevenue}
            isEditing={!!editingRevenue}
            startupStartDate={dataWithCustomerProjections.startDate}
          />
        </Modal>
      )}

      {/* Event Modal */}
      {isEventModalOpen && (
        <Modal
          isOpen={isEventModalOpen}
          onClose={() => {
            setIsEventModalOpen(false);
            setEditingEvent(null);
          }}
          title={editingEvent ? "Edit Event" : "Add Event"}
        >
          <EventForm
            initialData={
              editingEvent
                ? {
                    description: editingEvent.description || editingEvent.title || "",
                    priority: editingEvent.priority,
                    amount: editingEvent.amount,
                    icon: (editingEvent as { icon?: string }).icon || "Calendar",
                    color: editingEvent.color,
                    monthlyAppreciationRate: editingEvent.monthlyAppreciationRate,
                    minimumValue: editingEvent.minimumValue,
                    capacityGroupId: editingEvent.capacityGroupId,
                    projectionId: editingEvent.projectionId,
                    capacityIncrease: editingEvent.capacityIncrease,
                    date:
                      editingEvent.date instanceof Date
                        ? editingEvent.date.toISOString().split("T")[0]
                        : String(editingEvent.date).split("T")[0],
                    type:
                      editingEvent.type === "funding-needed"
                        ? "funding"
                        : editingEvent.type === "product-launch"
                        ? "launch"
                        : editingEvent.type === "meeting"
                        ? "other"
                        : editingEvent.type,
                  }
                : latestTimelineSnapshot
                ? {
                    date: latestTimelineSnapshot.date
                      .toISOString()
                      .split("T")[0],
                  }
                : currentSnapshot
                ? {
                    date: currentSnapshot.date.toISOString().split("T")[0],
                  }
                : undefined
            }
            onSubmit={handleEventSubmit}
            onDelete={
              editingEvent
                ? () => handleEventDelete(editingEvent.id)
                : undefined
            }
            onRevise={openAssetRevision}
            canRevise={
              !!reviseTargetForEditingEvent &&
              reviseTargetForEditingEvent.id === editingEvent?.id
            }
            onCancel={() => {
              setIsEventModalOpen(false);
              setEditingEvent(null);
            }}
            isEditing={!!editingEvent}
          />
        </Modal>
      )}

      {isAssetRevisionModalOpen && revisingAsset ? (
        <Modal
          isOpen={isAssetRevisionModalOpen}
          onClose={() => {
            setIsAssetRevisionModalOpen(false);
            setRevisingAsset(null);
          }}
          title="Revise asset"
        >
          <AssetRevisionForm
            asset={revisingAsset}
            defaultEffectiveDate={
              latestTimelineSnapshot?.date.toISOString().split("T")[0] ??
              currentSnapshot?.date.toISOString().split("T")[0]
            }
            onSubmit={handleAssetRevisionSubmit}
            onCancel={() => {
              setIsAssetRevisionModalOpen(false);
              setRevisingAsset(null);
            }}
            isLoading={reviseAssetMutation.isPending}
          />
        </Modal>
      ) : null}

      {isRevenueRevisionModalOpen && revisingRevenue ? (
        <Modal
          isOpen={isRevenueRevisionModalOpen}
          onClose={() => {
            setIsRevenueRevisionModalOpen(false);
            setRevisingRevenue(null);
          }}
          title="Revise revenue"
        >
          <RevenueRevisionForm
            revenue={revisingRevenue}
            defaultEffectiveDate={
              latestTimelineSnapshot?.date.toISOString().split("T")[0] ??
              currentSnapshot?.date.toISOString().split("T")[0]
            }
            onSubmit={handleRevenueRevisionSubmit}
            onCancel={() => {
              setIsRevenueRevisionModalOpen(false);
              setRevisingRevenue(null);
            }}
            isLoading={reviseRevenueMutation.isPending}
          />
        </Modal>
      ) : null}

      {isExpenseRevisionModalOpen && revisingExpense ? (
        <Modal
          isOpen={isExpenseRevisionModalOpen}
          onClose={() => {
            setIsExpenseRevisionModalOpen(false);
            setRevisingExpense(null);
          }}
          title="Revise expense"
        >
          <ExpenseRevisionForm
            expense={revisingExpense}
            defaultEffectiveDate={
              latestTimelineSnapshot?.date.toISOString().split("T")[0] ??
              currentSnapshot?.date.toISOString().split("T")[0]
            }
            contextMonthlyRevenue={currentSnapshot?.monthlyRevenue ?? 0}
            onSubmit={handleExpenseRevisionSubmit}
            onCancel={() => {
              setIsExpenseRevisionModalOpen(false);
              setRevisingExpense(null);
            }}
            isLoading={reviseExpenseMutation.isPending}
          />
        </Modal>
      ) : null}

      {/* Customer Projection Modal */}
      <Modal
        isOpen={isCustomerModalOpen}
        onClose={() => {
          setIsCustomerModalOpen(false);
          setEditingCustomerProjection(null);
          setCustomerProjectionSeed(null);
        }}
        title={
          editingCustomerProjection
            ? "Edit Customer Projection"
            : "Add Customer Projection"
        }
        size="lg"
      >
        <CustomerProjectionForm
          onSubmit={handleCustomerSubmit}
          onCancel={() => {
            setIsCustomerModalOpen(false);
            setEditingCustomerProjection(null);
            setCustomerProjectionSeed(null);
          }}
          onDelete={
            editingCustomerProjection
              ? () => handleCustomerDelete(editingCustomerProjection.id)
              : undefined
          }
          initialData={editingCustomerProjection || customerProjectionSeed || undefined}
          subscriptionTiers={subscriptionTiers}
          capacityGroups={capacityGroups}
          isLoading={
            createCustomerMutation.isPending ||
            updateCustomerMutation.isPending ||
            deleteCustomerMutation.isPending
          }
          isEditing={!!editingCustomerProjection}
        />
      </Modal>

      {/* Subscription Tier Modal */}
      <Modal
        isOpen={isSubscriptionTierModalOpen}
        onClose={() => {
          setIsSubscriptionTierModalOpen(false);
          setEditingSubscriptionTier(null);
        }}
        title={
          editingSubscriptionTier ? "Edit revenue plan" : "Add revenue plan"
        }
        size="lg"
      >
        <SubscriptionTierForm
          onSubmit={handleSubscriptionTierSubmit}
          onCancel={() => {
            setIsSubscriptionTierModalOpen(false);
            setEditingSubscriptionTier(null);
          }}
          onDelete={
            editingSubscriptionTier
              ? () => handleSubscriptionTierDelete(editingSubscriptionTier.id)
              : undefined
          }
          initialData={editingSubscriptionTier || undefined}
          isLoading={
            createSubscriptionTierMutation.isPending ||
            updateSubscriptionTierMutation.isPending ||
            deleteSubscriptionTierMutation.isPending
          }
          isEditing={!!editingSubscriptionTier}
        />
      </Modal>

      <Modal
        isOpen={isCapacityGroupModalOpen}
        onClose={() => {
          setIsCapacityGroupModalOpen(false);
          setEditingCapacityGroup(null);
        }}
        title={editingCapacityGroup ? "Edit capacity group" : "Add capacity group"}
      >
        <CapacityGroupForm
          key={editingCapacityGroup?.id ?? "new"}
          onSubmit={handleCapacityGroupSubmit}
          onCancel={() => {
            setIsCapacityGroupModalOpen(false);
            setEditingCapacityGroup(null);
          }}
          onDelete={
            editingCapacityGroup
              ? () => handleCapacityGroupDelete(editingCapacityGroup.id)
              : undefined
          }
          initialData={editingCapacityGroup || undefined}
          isLoading={
            createCapacityGroupMutation.isPending ||
            updateCapacityGroupMutation.isPending ||
            deleteCapacityGroupMutation.isPending
          }
          isEditing={!!editingCapacityGroup}
        />
      </Modal>

      <BulkDateShiftModal
        isOpen={isBulkShiftOpen}
        onClose={() => setIsBulkShiftOpen(false)}
        expenses={rawExpenses}
        revenues={rawRevenues}
        startupStartDate={dataWithCustomerProjections.startDate}
        onApplyExpenseUpdates={handleBulkExpenseUpdates}
        onApplyRevenueUpdates={handleBulkRevenueUpdates}
      />
    </>
  );
};

export default BurndownChartDemo;
